/**
 * Functions related to the grid
 *
 */

import { TimeBlock, Time } from '../types/time';
import { DAY_LENGTH, timeToMinutes, minutesToTime } from './time';
import { ReportsStaffAllocation, StaffWarning } from '../types/staffAllocation';
import { Event } from '../types/event';
import { HourFormat } from '../types/brand';

/**
 * Returns a list of blocks in a time range, e.g. [{name: '9:00am', block: 0}, ...]
 * WARNING: Name is changed from SET!!!
 */
export function getBlocksInRange(
  startTime: number,
  endTime: number,
  timegap: number,
  offset: number,
  hourFormat: HourFormat
): TimeBlock[] {
  let times = [];
  let blocks = (endTime - startTime) / timegap;

  for (let index = 0; index < blocks; index++) {
    times.push({ index, name: getBlockName(index, timegap, offset, hourFormat) });
  }

  return times;
}

/**
 * Convert a block index (0 - ...) to a date representation (e.g., 9:00am)
 * WARNING: Name is changed from SET!!!
 */
export function getBlockName(
  blockIndex: number,
  timegap: number,
  offset: number,
  hourFormat: HourFormat
) {
  let date = new Date();

  date.setHours(0, 0, 0, 0);
  date.setMinutes(timegap * blockIndex + offset);

  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    hour12: hourFormat === '12',
    minute: 'numeric',
  });
}

/**
 * Convert an object of the form {hour, minute} to a block index
 * @param time the time to convert
 * @param timeGap the gap between time blocks
 * @param dayOffset (optional) the day start offset, in minutes
 * @returns a block index where `time` falls
 */
export function timeToBlockIndex(time: Time, timeGap: number, dayOffset?: number): number {
  let blocksInDay = DAY_LENGTH / timeGap;
  dayOffset = dayOffset || 0;

  let timeBlock = Math.floor(timeToMinutes(time) / timeGap) - Math.floor(dayOffset / timeGap);

  // Adjust for negative numbers
  timeBlock = (timeBlock + blocksInDay) % blocksInDay;
  return timeBlock;
}

/**
 * Converts a block index to an {hour: h, minute: m} structure
 * @param blockIndex the block index
 * @param timeGap the gap between time blocks
 * @param dayOffset (optional) the day start offset, in minutes
 * @returns a `Time` object representing the block start
 */
export function blockToTime(blockIndex: number, timeGap: number, dayOffset?: number): Time {
  dayOffset = dayOffset || 0;
  let minutes = timeGap * blockIndex + dayOffset;
  return minutesToTime(minutes);
}

/**
 * Given an Events array, get all the staff in an array with the eventName associated
 * @param events Events from where staff will be retrieved
 */
export function getAllStaffInEvents(events: Event[]): ReportsStaffAllocation[] {
  let staff: ReportsStaffAllocation[] = [];

  events.forEach((e: Event) => {
    staff = staff.concat(
      e.staffAllocations.map(s => {
        return {
          ...s,
          ...{
            eventName: e.template.title,
            eventStartTime: e.startTime,
            venueName: e.venue.name,
            eventDuration: e.duration,
          },
        };
      })
    );
  });

  return staff;
}

export function checkForStaffWarnings(events: Event[], timeOffset: number): StaffWarning[] {
  const venueWarnings: StaffWarning[] = [];
  let staffs: StaffWarning[] = [];
  events.forEach(e => {
    staffs = staffs.concat(
      e.staffAllocations.map(staff => ({
        ...staff,
        eventStart: e.startTime,
        eventStartMinutes: timeToMinutes(e.startTime, timeOffset),
        eventName: e.template.title,
        venueName: e.venue.name,
        eventId: e._id,
      }))
    );
  });

  staffs.forEach(staff => {
    const s1start = staff.eventStartMinutes + staff.startTime;
    const s1final = s1start + staff.duration;

    staffs.forEach(staff2 => {
      if (staff._id !== staff2._id) {
        const s2start = staff2.eventStartMinutes + staff2.startTime;
        const s2final = s2start + staff2.duration;
        if (s1start < s2final && s2start < s1final && staff.template._id === staff2.template._id) {
          venueWarnings.push(staff);
        }
      }
    });
  });

  return venueWarnings;
}

// ----

//   /**
//    * Given an event with a startDay, endDay, startTime and endTime, and given a
//    * start day offset, return the duration in minutes for the event.
//    * FIXME: Some code is repeated from `apiEventToGrid`, refactor
//    *
//    */
//   export function calculateEventDuration(event, project) {
//     // TODO: Should not be hardcoded?
//     let dayLengthMinutes = 24 * 60;

//     let dayOffsetMinutes = TimeService.timeToMinutes(project.dayStart);
//     let startMinutes = TimeService.timeToMinutes(event.startTime);
//     let endMinutes = TimeService.timeToMinutes(event.endTime);

//     let startMinutesSinceBeginning = TimeService.minutesSinceDayStart(
//       startMinutes,
//       dayOffsetMinutes
//     );
//     let endMinutesSinceBeginning = TimeService.minutesSinceDayStart(endMinutes,
// dayOffsetMinutes);

//     let duration = endMinutesSinceBeginning - startMinutesSinceBeginning;

//     let startDayIndex = project.days.findIndex(d => d._id === event.startDay._id);
//     let endDayIndex = project.days.findIndex(d => d._id === event.endDay._id);

//     // 0-based days spanned between start and end
//     let daySpan = endDayIndex - startDayIndex;

//     duration += dayLengthMinutes * daySpan;

//     // NOTE: In some cases we represent an event as finishing on the same day at
//     // the last possible time, for example going from 5am to 6am on day 2. This
//     // is not really valid or consistent with reality, since if the end time is
//     // 6am, the end day should be the day after. However, for example when a
//     // user enters the values in a form the right interpretation would be
//     // counter-intuitive. Therefore, this is a hack for that boundary case so
//     // that we don't return a negative duration (i.e., when the end hour shows
//     // as the same day as the last allowable time, but it really is the first
//     // possible time of the *next* day)
//     if (duration < 0 && daySpan === 0 && endMinutesSinceBeginning === 0) {
//       duration += dayLengthMinutes;
//     }

//     return duration;
//   }

//   /**
//    * Given a time with the format {hour: h, minute: m} and a number of minutes,
//    * return the number of days spanned by the end date (0-based)
//    *
//    */
//   export function calculateDaySpan(time, minutes, project) {
//     let dayLengthMinutes = 24 * 60;
//     let dayOffsetMinutes = TimeService.timeToMinutes(project.dayStart);
//     let timeMinutes = TimeService.timeToMinutes(time);
//     let minutesSinceDayStart = TimeService.minutesSinceDayStart(timeMinutes, dayOffsetMinutes);
//     let endTimeMinutes = minutesSinceDayStart + minutes;

//     // Calculate how many days the event spans (0 based)
//     let dayOffset = Math.floor(endTimeMinutes / dayLengthMinutes);

//     // Adjust for events that end just at the end of day
//     if (endTimeMinutes === dayLengthMinutes) {
//       dayOffset = 0;
//     }

//     return dayOffset;
//   }

//   /**
//    * Converts an event returned by the API to an event as used by the grid
//    *
//    */
//   export function apiEventToGrid: function(
//     event,
//     project,
//     eventTemplates,
//     timeGap,
//     dayOffset,
//     dayStartTime,
//     dayEndTime,
//     staffTemplates,
//     cellHeight
//   ) {
//     let eventTemplate = eventTemplates.find(t => t._id === event.template);

//     if (!eventTemplate) {
//       return Notify.error('Found orphan event');
//     }

//     // This is just an assertion to ensure the day has 24 hours, which is
//     // assumed in the following code
//     let dayLengthMinutes = dayEndTime - dayStartTime;
//     if (dayLengthMinutes !== 24 * 60) {
//       Notify.error('Number of hours in day is not 24!');
//       return;
//     }

//     let startTimeMinutes = TimeService.timeToMinutes(event.startTime);
//     let endTimeMinutes = startTimeMinutes + event.duration;

//     let endTime = TimeService.minutesToTime(endTimeMinutes);

//     // offset in blocks since beginning of day
//     let dayOffsetBlock = Math.floor(dayOffset / timeGap);
//     let totalBlocks = dayLengthMinutes / timeGap;

//     let startBlock =
//       (Math.floor(startTimeMinutes / timeGap) + totalBlocks - dayOffsetBlock) % totalBlocks;
//     let endBlock =
//       (Math.floor(endTimeMinutes / timeGap) + totalBlocks - dayOffsetBlock) % totalBlocks;

//     // SPECIAL CASE: Detect an event that goes over the day boundary
//     let eventDaySpan = this.calculateDaySpan(event.startTime, event.duration, project);

//     // Adjust the end block
//     endBlock += totalBlocks * eventDaySpan;

//     // offset from the beginning of the block, in pixels
//     let startBlockOffset = Math.floor(((startTimeMinutes % timeGap) / timeGap) * cellHeight);
//     let endBlockOffset = Math.floor(((endTimeMinutes % timeGap) / timeGap) * cellHeight);

//     let startDayIndex = project.days.findIndex(d => d._id === event.day);

//     if (startDayIndex + eventDaySpan > project.days.length - 1) {
//       Notify.error('End day for event is out of bounds');
//       return;
//     }
//     let endDayIndex = startDayIndex + eventDaySpan;

//     // if final day after offset exceeds boundaries, show error
//     if (startDayIndex < 0 || startDayIndex > project.days.length - 1) {
//       Notify.error('Cannot draw event in project due to grid time layout');
//       return;
//     }

//     // convert staff ID to staff objects
//     let staffAllocations = event.staffAllocations.map(staff =>
//       this.apiStaffAllocationToModal(staff, staffTemplates)
//     );

//     return {
//       _id: event._id,
//       template: eventTemplate,
//       name: eventTemplate.title,
//       venue: project.venues.find(v => v._id === event.venue),
//       startDay: project.days[startDayIndex],
//       endDay: project.days[endDayIndex],
//       color: this.getEventColor(eventTemplate.segment, project),
//       from: startBlock,
//       to: endBlock,
//       startTime: event.startTime,
//       duration: event.duration,
//       startBlockOffset: startBlockOffset,
//       endTime: endTime,
//       endBlockOffset: endBlockOffset,
//       staffAllocations: staffAllocations,
//     };
//   }

//   export function apiStaffAllocationToModal(staff, staffTemplates) {
//     return {
//       template: staffTemplates.find(s => s._id === staff.template),
//       quantity: staff.quantity,
//       startTime: staff.startTime,
//       endTime: staff.startTime + staff.duration,
//       activity: staff.activity,
//     };
//   }

//   /**
//    * Converts a grid event, as defined in the modal dialog, to the API format
//    */
//   export function modalGridEventToApi(event, project) {
//     let startTime = TimeService.timeToMinutes(event.startTime);
//     let dayIndex = project.days.findIndex(d => d._id === event.startDay._id);

//     // if final day after offset exceeds boundaries, show error
//     if (dayIndex < 0 || dayIndex > project.days.length - 1) {
//       Notify.error('Cannot draw event in project due to grid time layout');
//       return;
//     }

//     let duration = this.calculateEventDuration(event, project);

//     // convert staff allocations to api format
//     let staffAllocations = event.staffAllocations.map(alloc => {
//       return {
//         template: alloc.template._id,
//         startTime: alloc.startTime,
//         duration: alloc.endTime - alloc.startTime,
//         activity: alloc.activity,
//         quantity: alloc.quantity,
//       };
//     });

//     let apiEvent = {
//       template: event.template._id,
//       venue: event.venue._id,
//       day: project.days[dayIndex],
//       startTime: startTime,
//       duration: duration,
//       staffAllocations: staffAllocations,
//     };

//     if (event._id) apiEvent._id = event._id;

//     return apiEvent;
//   }

//   /**
//    * Converts a grid event from the internal list of events, to the API format
//    */
//   export function gridEventToApi(
//     event,
//     project,
//     eventTemplates,
//     timeGap,
//     offset,
//     dayStartTime,
//     dayEndTime
//   ) {
//     let eventTime = TimeService.timeToMinutes(event.startTime);
//     let dayIndex = project.days.findIndex(d => d._id === event.startDay._id);

//     if (eventTime >= dayEndTime) {
//       eventTime -= dayEndTime;
//       dayIndex++;
//     } else if (eventTime < dayStartTime) {
//       eventTime += dayEndTime;
//       dayIndex++;
//     }

//     // if final day after offset exceeds boundaries, show error
//     if (dayIndex < 0 || dayIndex > project.days.length - 1) {
//       Notify.error('Cannot insert event in project due to grid time layout');
//       return;
//     }

//     // convert staff allocations to api format
//     let staffAllocations = event.staffAllocations.map(alloc => {
//       return {
//         template: alloc.template._id,
//         startTime: alloc.startTime,
//         duration: alloc.endTime - alloc.startTime,
//         activity: alloc.activity,
//         quantity: alloc.quantity,
//       };
//     });

//     let apiEvent = {
//       template: event.template._id,
//       venue: event.venue._id,
//       day: project.days[dayIndex],
//       startTime: eventTime,
//       duration: event.duration,
//       staffAllocations: staffAllocations,
//     };

//     if (event._id) apiEvent._id = event._id;

//     return apiEvent;
//   }

//   /**
//    * Retrieves a color from brand definitions
//    */
// export function getEventColor(segment, project) {
//     if (!segment) return DEFAULT_EVENT_COLOR;

//     let brandSegment = project.property.brand.segments.find(s => s._id === segment);
//     return brandSegment ? brandSegment.color : DEFAULT_EVENT_COLOR;
//   }

//   /**
//    * Calculate the distance in pixels between two points
//    *
//    */
//   export function distance(x1, y1, x2, y2) {
//     return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
//   }
